import { useState, type FormEvent } from 'react';

import {
    Avatar,
    Button,
    Card,
    Dropdown,
    Field,
    Input,
    MonoLabel,
    OptionGroup,
    OptionRow,
    Skeleton,
    StatusPill,
} from '@/design-system';
import {
    ASSIGNABLE_ROLES,
    initialsFor,
    MEMBERSHIP_STATUS_LABELS,
    PERMISSIONS,
    ROLE_CAPABILITIES,
    ROLE_SUMMARY,
    sortRoles,
    type Invitation,
    type MembershipStatus,
    type TeamMember,
    type TenantRole,
} from '@/entities/user';
import { errorMessage, isApiError, problemKey, ROLE_LABELS } from '@/shared/api';
import { formatDayShort } from '@/shared/lib';
import { ErrorState, useWorkspaceAccess } from '@/shared/ui';

import { rolesText, teamCopy } from '../copy';
import {
    useDisableMember,
    useEnableMember,
    useInvitations,
    useInvite,
    useResendInvitation,
    useSetRoles,
    useTeamMembers,
    useWithdrawInvitation,
} from '../hooks';

const STATUS_ORDER: Record<MembershipStatus, number> = { active: 0, invited: 1, disabled: 2 };

const TEXT_SMALL = 'text-[12px] leading-[1.45] text-[var(--text-tertiary-safe)]';

/**
 * Gebruikers (scherm 11, rechterkolom): het team van dit bedrijf.
 *
 * Wat hier staat zijn de mensen met een lidmaatschap van **dit** bedrijf, en de
 * uitnodigingen die nog openstaan. Een keurmeester kan bij meerdere
 * keuringsbedrijven horen, maar dit scherm laat daar niets van zien.
 *
 * Wat je hier kunt, volgt je rechten: uitnodigen, opnieuw versturen en intrekken
 * met `users.invite`, rollen wijzigen, uitschakelen en weer toelaten met
 * `users.manage`. De eigenaar heeft ze allebei. Je eigen rollen en toegang wijzig
 * je nooit zelf.
 */
export function TeamCard() {
    const access = useWorkspaceAccess();
    const canView = access.can(PERMISSIONS.usersView);
    const canInvite = access.can(PERMISSIONS.usersInvite);
    const canManage = access.can(PERMISSIONS.usersManage);

    const members = useTeamMembers(canView);
    const invitations = useInvitations(canView);

    if (access.loading || (canView && members.isPending)) {
        return (
            <Card padding="lg" aria-busy="true">
                <Skeleton width="45%" height={15} label={`${teamCopy.title} laden`} />
                <Skeleton height={14} className="mt-3" />
                <Skeleton width="70%" height={14} className="mt-2" />
            </Card>
        );
    }

    if (!canView) {
        return null;
    }

    if (members.isError) {
        return (
            <Card padding="lg">
                <ErrorState
                    error={members.error}
                    title={teamCopy.title}
                    onRetry={() => void members.refetch()}
                />
            </Card>
        );
    }

    const sorted = [...(members.data ?? [])].sort(
        (a, b) =>
            STATUS_ORDER[a.status] - STATUS_ORDER[b.status] || a.name.localeCompare(b.name, 'nl'),
    );

    return (
        <Card padding="lg" as="section" aria-labelledby="team-title">
            <h2
                id="team-title"
                className="m-0 font-[family-name:var(--font-display)] text-[15px] leading-[1.3] font-semibold text-[var(--text-primary)]"
            >
                {teamCopy.title}
            </h2>
            <p className={`mt-1 mb-3 ${TEXT_SMALL}`}>{teamCopy.intro}</p>

            {sorted.length === 0 ? (
                <p className="m-0 py-[9px] text-[13px] leading-[1.5] text-[var(--text-tertiary-safe)]">
                    {teamCopy.empty}
                </p>
            ) : (
                <ul className="m-0 list-none p-0">
                    {sorted.map((member) => (
                        <MemberRow
                            key={member.uuid}
                            member={member}
                            self={member.uuid === access.userUuid}
                            canManage={canManage}
                        />
                    ))}
                </ul>
            )}

            {invitations.data && (canInvite || invitations.data.length > 0) ? (
                <InvitationList invitations={invitations.data} canInvite={canInvite} />
            ) : null}

            {canInvite ? <InviteForm /> : null}
        </Card>
    );
}

/** De melding onder een mislukte handeling, in de woorden van dit scherm. */
function teamError(error: unknown): string {
    return problemKey(error) === 'last-owner-protected' ? teamCopy.lastOwner : errorMessage(error);
}

function MemberRow({
    member,
    self,
    canManage,
}: {
    member: TeamMember;
    self: boolean;
    canManage: boolean;
}) {
    const [mode, setMode] = useState<'idle' | 'roles' | 'disable'>('idle');
    const [draft, setDraft] = useState<TenantRole[]>(member.roles);
    const [notice, setNotice] = useState<string | null>(null);
    const setRoles = useSetRoles();
    const disable = useDisableMember();
    const enable = useEnableMember();

    const manageable = canManage && !self && member.status === 'active';
    /* Dezelfde regels als uitschakelen, voor wie is uitgeschakeld. */
    const enableable = canManage && !self && member.status === 'disabled';
    const error = setRoles.error ?? disable.error;

    function toggleRole(role: TenantRole, on: boolean) {
        setDraft((current) =>
            sortRoles(on ? [...new Set([...current, role])] : current.filter((r) => r !== role)),
        );
    }

    return (
        <li className="border-b border-[var(--border-subtle-2)] py-[9px]">
            <div className="flex items-center gap-[11px]">
                <Avatar size={30}>{initialsFor(member.name || member.email)}</Avatar>

                <div className="min-w-0 flex-1">
                    <div className="truncate text-[14px] leading-[1.35] text-[var(--text-primary)]">
                        {member.name || member.email}
                        {self ? (
                            <span className="text-[var(--text-tertiary-safe)]">{` · ${teamCopy.you}`}</span>
                        ) : null}
                    </div>
                    <div className="truncate text-[12px] leading-[1.4] text-[var(--text-tertiary-safe)]">
                        {`${member.roles.length > 0 ? rolesText(member.roles) : teamCopy.noRole} · ${member.email}`}
                    </div>
                </div>

                <MonoLabel size={10} tone="muted">
                    {MEMBERSHIP_STATUS_LABELS[member.status]}
                </MonoLabel>
            </div>

            <div className="mt-[4px] flex flex-wrap items-center gap-x-3 gap-y-1 pl-[41px]">
                {member.status === 'disabled' ? (
                    <span className={TEXT_SMALL}>{teamCopy.disabledNote}</span>
                ) : member.joinedAt ? (
                    <span className={TEXT_SMALL}>
                        {teamCopy.memberSince(formatDayShort(member.joinedAt))}
                    </span>
                ) : null}
            </div>

            {manageable && mode === 'idle' ? (
                <div className="mt-[8px] flex flex-wrap gap-2 pl-[41px]">
                    <Button
                        variant="outline"
                        size="xs"
                        onClick={() => {
                            setDraft(member.roles);
                            setNotice(null);
                            setRoles.reset();
                            setMode('roles');
                        }}
                    >
                        {teamCopy.changeRoles}
                    </Button>
                    <Button
                        variant="outline"
                        size="xs"
                        onClick={() => {
                            setNotice(null);
                            disable.reset();
                            setMode('disable');
                        }}
                    >
                        {teamCopy.disable}
                    </Button>
                </div>
            ) : null}

            {enableable && mode === 'idle' ? (
                <div className="mt-[8px] flex flex-wrap gap-2 pl-[41px]">
                    <Button
                        variant="outline"
                        size="xs"
                        disabled={enable.isPending}
                        onClick={() => {
                            setNotice(null);
                            enable.mutate(member.uuid, {
                                onSuccess: () =>
                                    setNotice(teamCopy.enabled(member.name || member.email)),
                            });
                        }}
                    >
                        {teamCopy.enable}
                    </Button>
                </div>
            ) : null}

            {manageable && mode === 'roles' ? (
                <div className="mt-[10px] rounded-[9px] border border-[var(--border-subtle)] bg-[var(--surface-subtle)] p-[12px]">
                    <p className="m-0 text-[12px] leading-[1.4] text-[var(--text-secondary)]">
                        {teamCopy.rolesLabel(member.name || member.email)}
                    </p>
                    <p className={`m-0 mt-[2px] ${TEXT_SMALL}`}>{teamCopy.rolesHint}</p>

                    <OptionGroup
                        type="check"
                        label={teamCopy.rolesLabel(member.name || member.email)}
                        className="mt-2 px-0"
                    >
                        {ASSIGNABLE_ROLES.map((role, index) => (
                            <OptionRow
                                key={role}
                                index={index}
                                checked={draft.includes(role)}
                                onSelect={(on) => toggleRole(role, on)}
                            >
                                <span className="block">{ROLE_LABELS[role]}</span>
                                <span className="block text-[12px] leading-[1.4] font-normal text-[var(--text-tertiary-safe)]">
                                    {ROLE_SUMMARY[role]}
                                </span>
                            </OptionRow>
                        ))}
                    </OptionGroup>

                    <div className="mt-[10px] flex flex-wrap items-center gap-2">
                        <Button
                            variant="primary"
                            size="sm"
                            disabled={setRoles.isPending || draft.length === 0}
                            onClick={() =>
                                setRoles.mutate(
                                    { userUuid: member.uuid, roles: draft },
                                    {
                                        onSuccess: () => {
                                            setMode('idle');
                                            setNotice(
                                                teamCopy.rolesSaved(member.name || member.email),
                                            );
                                        },
                                    },
                                )
                            }
                        >
                            {teamCopy.rolesSave}
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setMode('idle')}>
                            {teamCopy.cancel}
                        </Button>
                        {draft.length === 0 ? (
                            <span className={TEXT_SMALL}>{teamCopy.rolesEmpty}</span>
                        ) : null}
                    </div>
                </div>
            ) : null}

            {manageable && mode === 'disable' ? (
                <Card
                    variant="warn"
                    padding="md"
                    radius={9}
                    className="mt-[10px]"
                    role="group"
                    aria-label={teamCopy.disableLabel(member.name || member.email)}
                >
                    <p className="m-0 text-[13px] leading-[1.5] text-[var(--warn-ink)]">
                        {teamCopy.disableBody(member.name || member.email)}
                    </p>
                    <div className="mt-[10px] flex flex-wrap gap-2">
                        <Button
                            variant="warn"
                            size="sm"
                            disabled={disable.isPending}
                            onClick={() =>
                                disable.mutate(member.uuid, {
                                    onSuccess: () => {
                                        setMode('idle');
                                        setNotice(teamCopy.disabled(member.name || member.email));
                                    },
                                })
                            }
                        >
                            {teamCopy.disableYes}
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setMode('idle')}>
                            {teamCopy.cancel}
                        </Button>
                    </div>
                </Card>
            ) : null}

            {notice ? (
                <p
                    role="status"
                    className="m-0 mt-2 pl-[41px] text-[12px] leading-[1.4] text-[var(--accent-dark)]"
                >
                    {notice}
                </p>
            ) : null}

            {error && mode !== 'idle' ? (
                <p role="alert" className="m-0 mt-2 text-[12px] leading-[1.4] text-[var(--danger)]">
                    {teamError(error)}
                </p>
            ) : null}

            {enable.error && mode === 'idle' ? (
                <p
                    role="alert"
                    className="m-0 mt-2 pl-[41px] text-[12px] leading-[1.4] text-[var(--danger)]"
                >
                    {teamError(enable.error)}
                </p>
            ) : null}
        </li>
    );
}

function InvitationList({
    invitations,
    canInvite,
}: {
    invitations: readonly Invitation[];
    canInvite: boolean;
}) {
    return (
        <section aria-labelledby="invitations-title" className="mt-[14px]">
            <h3
                id="invitations-title"
                className="m-0 mb-1 font-[family-name:var(--font-display)] text-[13px] leading-[1.3] font-semibold text-[var(--text-primary)]"
            >
                {teamCopy.invitationsTitle}
            </h3>

            {invitations.length === 0 ? (
                <p className={`m-0 py-[6px] ${TEXT_SMALL}`}>{teamCopy.invitationsEmpty}</p>
            ) : (
                <ul className="m-0 list-none p-0">
                    {invitations.map((invitation) => (
                        <InvitationRow
                            key={invitation.uuid}
                            invitation={invitation}
                            canInvite={canInvite}
                        />
                    ))}
                </ul>
            )}
        </section>
    );
}

function InvitationRow({ invitation, canInvite }: { invitation: Invitation; canInvite: boolean }) {
    const [confirming, setConfirming] = useState(false);
    const [notice, setNotice] = useState<string | null>(null);
    const resend = useResendInvitation();
    const withdraw = useWithdrawInvitation();
    const error = resend.error ?? withdraw.error;
    const role = invitation.role ? ROLE_LABELS[invitation.role] : invitation.roleLabel;
    const validity = invitation.expired
        ? teamCopy.invitationExpired(formatDayShort(invitation.expiresAt))
        : teamCopy.invitationValid(formatDayShort(invitation.expiresAt));

    return (
        <li className="border-b border-[var(--border-subtle-2)] py-[9px]">
            <div className="flex flex-wrap items-center gap-x-[11px] gap-y-1">
                <div className="min-w-0 flex-1">
                    <div className="truncate text-[14px] leading-[1.35] text-[var(--text-primary)]">
                        {invitation.name ?? invitation.email}
                    </div>
                    <div className="truncate text-[12px] leading-[1.4] text-[var(--text-tertiary-safe)]">
                        {[
                            invitation.name ? invitation.email : null,
                            role,
                            invitation.invitedByName
                                ? teamCopy.invitationBy(invitation.invitedByName)
                                : null,
                        ]
                            .filter(Boolean)
                            .join(' · ')}
                    </div>
                </div>
                <StatusPill tone={invitation.expired ? 'warn' : 'info'} size="sm">
                    {validity}
                </StatusPill>
            </div>

            {canInvite && !confirming ? (
                <div className="mt-[8px] flex flex-wrap gap-2">
                    <Button
                        variant="outline"
                        size="xs"
                        disabled={resend.isPending}
                        onClick={() => {
                            setNotice(null);
                            withdraw.reset();
                            resend.mutate(invitation.uuid, {
                                onSuccess: (renewed) =>
                                    setNotice(
                                        teamCopy.resent(
                                            renewed.email,
                                            formatDayShort(renewed.expiresAt),
                                        ),
                                    ),
                            });
                        }}
                    >
                        {teamCopy.resend}
                    </Button>
                    <Button
                        variant="outline"
                        size="xs"
                        onClick={() => {
                            setNotice(null);
                            resend.reset();
                            setConfirming(true);
                        }}
                    >
                        {teamCopy.withdraw}
                    </Button>
                </div>
            ) : null}

            {canInvite && confirming ? (
                <Card
                    variant="warn"
                    padding="md"
                    radius={9}
                    className="mt-[10px]"
                    role="group"
                    aria-label={teamCopy.withdrawLabel(invitation.email)}
                >
                    <p className="m-0 text-[13px] leading-[1.5] text-[var(--warn-ink)]">
                        {teamCopy.withdrawBody(invitation.email)}
                    </p>
                    <div className="mt-[10px] flex flex-wrap gap-2">
                        <Button
                            variant="warn"
                            size="sm"
                            disabled={withdraw.isPending}
                            onClick={() => withdraw.mutate(invitation.uuid)}
                        >
                            {teamCopy.withdrawYes}
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setConfirming(false)}>
                            {teamCopy.cancel}
                        </Button>
                    </div>
                </Card>
            ) : null}

            {notice ? (
                <p
                    role="status"
                    className="m-0 mt-2 text-[12px] leading-[1.4] text-[var(--accent-dark)]"
                >
                    {notice}
                </p>
            ) : null}

            {error ? (
                <p role="alert" className="m-0 mt-2 text-[12px] leading-[1.4] text-[var(--danger)]">
                    {errorMessage(error)}
                </p>
            ) : null}
        </li>
    );
}

function InviteForm() {
    const [email, setEmail] = useState('');
    const [role, setRole] = useState<TenantRole>('inspector');
    const [emailError, setEmailError] = useState<string | null>(null);
    const invite = useInvite();

    const fieldError = (field: string) =>
        isApiError(invite.error) ? invite.error.firstValidationError(field) : undefined;
    const formError =
        invite.error && !fieldError('email') && !fieldError('role')
            ? errorMessage(invite.error)
            : null;

    function submit(event: FormEvent) {
        event.preventDefault();

        if (email.trim() === '') {
            setEmailError(teamCopy.emailRequired);

            return;
        }

        setEmailError(null);
        invite.mutate(
            { email: email.trim(), role },
            {
                onSuccess: () => setEmail(''),
            },
        );
    }

    return (
        <form
            onSubmit={submit}
            noValidate
            className="mt-[14px] border-t border-[var(--border-subtle)] pt-[14px]"
            aria-labelledby="invite-title"
        >
            <h3
                id="invite-title"
                className="m-0 mb-2 font-[family-name:var(--font-display)] text-[13px] leading-[1.3] font-semibold text-[var(--text-primary)]"
            >
                {teamCopy.inviteTitle}
            </h3>

            <div className="flex flex-wrap items-start gap-2">
                <Field
                    label={teamCopy.email}
                    size="sm"
                    className="min-w-[200px] flex-1"
                    error={emailError ?? fieldError('email')}
                >
                    <Input
                        type="email"
                        value={email}
                        autoComplete="off"
                        spellCheck={false}
                        placeholder={teamCopy.emailPlaceholder}
                        onChange={(event) => setEmail(event.target.value)}
                    />
                </Field>
                <Field
                    label={teamCopy.role}
                    size="sm"
                    className="min-w-[180px]"
                    error={fieldError('role')}
                >
                    <Dropdown
                        value={role}
                        options={ASSIGNABLE_ROLES.map((assignable) => ({
                            value: assignable,
                            label: ROLE_LABELS[assignable],
                        }))}
                        onChange={setRole}
                    />
                </Field>
                <div className="flex min-h-[64px] items-end">
                    <Button type="submit" variant="primary" size="sm" disabled={invite.isPending}>
                        {invite.isPending ? teamCopy.inviting : teamCopy.invite}
                    </Button>
                </div>
            </div>

            <RoleCapabilities role={role} />

            <p className={`m-0 mt-2 ${TEXT_SMALL}`}>{teamCopy.inviteNote}</p>

            {invite.isSuccess ? (
                <p
                    role="status"
                    className="m-0 mt-2 text-[12px] leading-[1.5] text-[var(--accent-dark)]"
                >
                    {teamCopy.invited(invite.data.email, formatDayShort(invite.data.expiresAt))}
                </p>
            ) : null}

            {formError ? (
                <p role="alert" className="m-0 mt-2 text-[12px] leading-[1.4] text-[var(--danger)]">
                    {formError}
                </p>
            ) : null}
        </form>
    );
}

/**
 * Wat deze rol mag, direct naast de keuze. Wie rollen uitdeelt moet de gevolgen
 * kunnen zien voordat hij op Uitnodigen drukt.
 */
function RoleCapabilities({ role }: { role: TenantRole }) {
    return (
        <div className="mt-[10px]">
            <div className="flex flex-wrap items-center gap-2">
                <StatusPill tone="info" size="sm">
                    {ROLE_LABELS[role]}
                </StatusPill>
                <span className="text-[12px] leading-[1.4] text-[var(--text-secondary)]">
                    {ROLE_SUMMARY[role]}
                </span>
            </div>
            <ul className="m-0 mt-[6px] list-none p-0">
                {ROLE_CAPABILITIES[role].map((capability) => (
                    <li
                        key={capability}
                        className="flex gap-2 py-[2px] text-[12px] leading-[1.45] text-[var(--text-tertiary-safe)]"
                    >
                        <span
                            aria-hidden="true"
                            className="mt-[6px] h-[5px] w-[5px] shrink-0 rounded-full bg-[var(--accent)]"
                        />
                        {capability}
                    </li>
                ))}
            </ul>
        </div>
    );
}
